/**
 * The earnings badge, as its own small texture.
 *
 * A plus can be drawn in the shader with two rectangles. A dollar sign cannot
 * — not at this size, not without it looking like something a shader author
 * approximated. So the badge is drawn once with Canvas 2D, where text is
 * text, and sampled like any other sprite.
 *
 * Deliberately NOT part of the card atlas. The cards are a settled design and
 * this is an overlay on top of them; keeping it in its own texture means the
 * wall's artwork is never touched to change a badge, and the badge can be
 * redrawn on a theme change without re-uploading 36MB of tiles.
 *
 * NO AMOUNT. It carried four invented figures for a while, which read as a
 * claim about what a creator earns — a number on a sign-in screen is a
 * promise nobody agreed to make. The mark says "you get paid for this", and
 * the amount belongs to the screens behind the login, where it is real.
 */

const W = 512;
const H = 256;
const LABEL = "+$";

function pill(
  c: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
) {
  const r = h / 2;
  c.beginPath();
  c.moveTo(x + r, y);
  c.arcTo(x + w, y, x + w, y + h, r);
  c.arcTo(x + w, y + h, x, y + h, r);
  c.arcTo(x, y + h, x, y, r);
  c.arcTo(x, y, x + w, y, r);
  c.closePath();
}

/**
 * `fill` is the design system's success green, resolved by the caller — the
 * shader gets a finished sprite rather than a colour to tint.
 */
export function buildBadge(family: string, fill: string): HTMLCanvasElement {
  const cv = document.createElement("canvas");
  cv.width = W;
  cv.height = H;
  const c = cv.getContext("2d");
  if (!c) return cv;

  c.font = `700 118px ${family}`;
  c.textAlign = "center";
  c.textBaseline = "middle";

  const h = 168;
  const w = Math.min(W - 40, c.measureText(LABEL).width + 132);
  const x = (W - w) / 2;
  const y = (H - h) / 2;

  // A soft drop shadow, so the pill survives landing on a bright tile without
  // needing a border drawn round it.
  c.save();
  c.shadowColor = "rgba(0,0,0,0.45)";
  c.shadowBlur = 26;
  c.shadowOffsetY = 6;
  c.fillStyle = fill;
  pill(c, x, y, w, h);
  c.fill();
  c.restore();

  c.fillStyle = "#ffffff";
  c.fillText(LABEL, W / 2, H / 2 + 4);

  return cv;
}
